import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { find as _find, get as _get, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

// Store
import {
  claimFnolQuery,
  DraftClaim,
  fromClaimFnolActions,
  ReportClaimFnolService,
  SelectOptions
} from '@amfam/claim/fnol/data-access';
import { PolicySelectors } from '@amfam/policy/data-access';
import { AnalyticsFacade, ClaimsAnalytics } from '@amfam/shared/analytics';
// Constants file for claims
import { ClaimsNavigationPaths, State } from '@amfam/shared/models';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ErrorObj, LoadingSpinnerService, ValidationService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { ClaimsFnolFormControls } from '../../shared/claims-constants';

@Component({
  selector: 'ds-report-claim-incident-location',
  templateUrl: './report-claim-incident-location.component.html',
  styleUrls: ['../report-claim-fnol.component.scss'],
  standalone: false
})
export class ReportClaimIncidentLocationComponent implements OnInit, OnDestroy {
  public reportIncidentLocationForm: UntypedFormGroup;
  public location: AbstractControl;
  public dateOfIncident: UntypedFormControl;
  public locationGroup: UntypedFormGroup;

  // Aggregate Loading Observable
  public loading: Observable<Boolean>;

  public pageTitleName = 'File a Claim';

  public usStates: State[];
  private draftClaim: DraftClaim;
  public errorList: Array<ErrorObj> = [];
  private claimNumber: string;
  public lossLocationTypes: Array<SelectOptions>;
  public isFormValid: Boolean = false;

  // Shared Subject for completing all observables on destroy
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService,
    private reportClaimFnolService: ReportClaimFnolService,
    private store: Store<fromRoot.RootState>,
    private spinner: LoadingSpinnerService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit() {
    this.spinner.start({
      blockActions: true
    });

    this.buildForm();

    this.loading = this.store.select(claimFnolQuery.getSelectedEntityLoading);

    this.isFormValid = this.reportIncidentLocationForm.valid;

    this.lossLocationTypes = this.copyService.get('claims', 'lossLocations');

    combineLatest(
      this.store.select(claimFnolQuery.getClaimFnolSelectedEntity),
      this.store.select(PolicySelectors.selectPolicies),
      (draftClaim, policies) => {
        return {
          draftClaim: draftClaim,
          policies: policies
        };
      }
    )
      .pipe(
        takeUntil(this.stop$),
        filter(state => {
          return state.draftClaim && !_some([state.draftClaim], { loading: true });
        })
      )
      .subscribe(state => {
        this.draftClaim = state.draftClaim;
        this.claimNumber = this.draftClaim.claimNumber;
        this.spinner.stop();
        if (this.locationGroup.valid) {
          /**
           * AS: If we have gotten a response from the api call after save & continue
           * we need to decide if we redirect to next screen or take the user to summary page.
           */
          if (_get(this.draftClaim, 'fnolDetail.typeOfLossCauseCode', undefined)) {
            /**
             * This mean we already have the information for the next screen, take the user to
             * the summary page then.
             */
            this.reportClaimFnolService.getRoutingPath(this.draftClaim);
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [ClaimsNavigationPaths.CLAIM_CAUSE_PATH]
              })
            );
          }
        }
        if (state.policies) {
          this.reportClaimFnolService.setClaimsPageTitle(
            state.policies.filter(policy => policy.policyNumber === this.draftClaim.policyNumber)[0]
          );
        }
      });

    /**
     * Condition to check required data is available to populate on landing from edit-risk-summary or via back button.
     */
    if (this.draftClaim && !!_get(this.draftClaim, 'fnolDetail.typeOfLossLocationCode', '')) {
      this.getSavedClaimInfo();
    }

    // Filter non-states from the list
    this.usStates = this.copyService.get('shared', 'usStates').filter(item => {
      return item && !item.territory;
    });

    const location = <UntypedFormGroup>this.reportIncidentLocationForm.controls['location'];
    this.analyticsFacade.trackPage(ClaimsAnalytics.pageAutoClaimIncidentLocation);
  }

  // onDestroy emit value and complete
  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  buildForm() {
    this.reportIncidentLocationForm = this.formBuilder.group({
      location: this.formBuilder.group({
        incidentCity: [
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(2),
            ValidationService.fnolCityValidator
          ])
        ],
        incidentState: ['', Validators.compose([Validators.required])],
        lossLocationType: ['', Validators.compose([Validators.required])],
        lossInjuries: ['', Validators.compose([Validators.required])]
      })
    });

    this.locationGroup = <UntypedFormGroup>this.reportIncidentLocationForm.controls['location'];
  }

  goBackOneStep() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_INCIDENT_TIME_PATH]
      })
    );
  }

  cancel() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_HOME_PATH]
      })
    );
  }

  private getSavedClaimInfo(): void {
    for (const control in this.locationGroup.controls) {
      if (control === ClaimsFnolFormControls.INCIDENT_CITY) {
        this.locationGroup.controls[ClaimsFnolFormControls.INCIDENT_CITY].setValue(
          this.draftClaim.lossLocationAddress.city
        );
      } else if (control === ClaimsFnolFormControls.INCIDENT_STATE) {
        this.locationGroup.controls[ClaimsFnolFormControls.INCIDENT_STATE].setValue(
          this.draftClaim.lossLocationAddress.state
        );
      } else if (control === ClaimsFnolFormControls.LOSS_LOCATION_TYPE) {
        const lossLocation: string = _find(this.lossLocationTypes, {
          value: this.draftClaim.fnolDetail.typeOfLossLocationCode
        }).value;
        this.locationGroup.controls[ClaimsFnolFormControls.LOSS_LOCATION_TYPE].setValue(
          lossLocation
        );
      } else if (control === ClaimsFnolFormControls.LOSS_INJURIES) {
        const injuryIndicator =
          String(_get(this.draftClaim, 'fnolDetail.injuryIndicator', '')) === 'true' ? 'yes' : 'no';
        this.locationGroup.controls[ClaimsFnolFormControls.LOSS_INJURIES].setValue(injuryIndicator);
      }
    }
  }

  /**
   * 1. Check lossInjuries value.
   *  1.1 Is selected as yes redirect unto callus component.
   *  1.2 Is seleceted as No -  Check claimNumber exists on draftClaim node level..
   *   1.2.1 Available - proceed with update FNOL operation
   *   1.2.2 Un Available - Do create FNOL operation and on ..
   *    1.2.2.1 Success - proceed with update FNOL operation
   *    1.2.2.2 Fail - Redirect to loss cause component(because update operation needs claimNumber).
   */

  public submitClaimLocation() {
    const updateClaimRequestObj = this.reportClaimFnolService.createUpdateDraftClaimLocationFactory(
      this.locationGroup.value,
      this.draftClaim
    );
    if (this.locationGroup.controls[ClaimsFnolFormControls.LOSS_INJURIES].value === 'yes') {
      this.store.dispatch(new fromClaimFnolActions.UpdateDraftClaim(updateClaimRequestObj));
      this.store.dispatch(
        fromRouterActions.Go({
          path: [ClaimsNavigationPaths.CLAIM_CALLUS_INJURY_PATH]
        })
      );
    } else {
      this.spinner.start({
        blockActions: true
      });
      if (!this.claimNumber) {
        const draftClaim: DraftClaim = Object.assign(
          {},
          this.draftClaim,
          updateClaimRequestObj.fnol
        );
        this.store.dispatch(new fromClaimFnolActions.CreateDraftClaim(draftClaim));
      } else {
        this.store.dispatch(new fromClaimFnolActions.UpdateDraftClaim(updateClaimRequestObj));
      }
    }
  }
}
